import { useState } from 'react';
import { EmailMessageClient, EmailMessageDTO, ApiResponse_string } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';
import { handleHookError } from '../utils/hookUtils/handleHookError';

export const useEmailMessageApi = () => {
    const apiClient = new EmailMessageClient(API_BASE_URL, axiosInstance);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async (data: EmailMessageDTO) => {
        setIsLoading(true);
        try {
            await apiClient.createEmailMessage(data);
        } catch (err: any) {
            setError(err.message || 'Error creating EmailMessage');
        } finally {
            setIsLoading(false);
        }
    };

    const edit = async (id: string, data: EmailMessageDTO) => {
        setIsLoading(true);
        try {
            await apiClient.updateEmailMessage(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating EmailMessage');
        } finally {
            setIsLoading(false);
        }
    };

    const getById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getEmailMessageById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching EmailMessage');
        } finally {
            setIsLoading(false);
        }
    };

    const search = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchEmailMessages(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching EmailMessages');
        } finally {
            setIsLoading(false);
        }
    };

    const searchEmailAndThreadMessages = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchEmailAndThreadMessages(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching Email and Thread Messages');
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.deleteEmailMessage(id);
        } catch (err: any) {
            setError(err.message || 'Error deleting EmailMessage');
        } finally {
            setIsLoading(false);
        }
    };

    const extract = async (
        filters: any
    ): Promise<ApiResponse_string> => {
        setIsLoading(true);
        try {
            return await apiClient.exportEmailMessages(filters);
        } catch (err: unknown) {
            handleHookError(err, 'Error exporting EmailMessages', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const sendEmail = async (data: EmailMessageDTO) => {
        setIsLoading(true);
        try {
            await apiClient.sendEmail(data);
        } catch (err: unknown) {
            handleHookError(err, 'Error sending Email', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };
    const createReply = async (data: EmailMessageDTO) => {
        setIsLoading(true);
        try {
            await apiClient.createEmailReply(data);
        } catch (err: unknown) {
            handleHookError(err, 'Error creating Email Reply', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    return { create, edit, getById, search, searchEmailAndThreadMessages, extract, remove, sendEmail, createReply, isLoading, error };
};